import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

/**
 * NavigationHeader component for handling navigation in the teacher grading interface
 *
 * @param {Object} props - Component properties
 * @param {Object} props.assignment - The assignment object
 * @param {string} props.studentName - Name of the student
 * @param {string} props.submission_id - Current submission ID
 * @param {string} props.class_id - Class ID
 * @param {string} props.assignment_id - Assignment ID
 * @param {Array} props.submissionIds - Array of all submission IDs
 * @param {Function} props.clickLeft - Function to navigate to next submission
 * @param {Function} props.clickRight - Function to navigate to previous submission
 * @param {boolean} props.isReviewed - Whether the submission is marked as reviewed
 * @param {Function} props.handleMarkReviewed - Function to toggle the reviewed status
 */
const NavigationHeader = ({
  assignment,
  studentName,
  submission_id,
  class_id,
  assignment_id,
  submissionIds,
  clickLeft,
  clickRight,
  isReviewed,
  handleMarkReviewed,
  teachers,
  pinnedSubQuestionId,
  navigateToPreviousSubQuestion,
  navigateToNextSubQuestion,
  submissionData
}) => {
  const [showAllReviewedPopup, setShowAllReviewedPopup] = useState(false);
  const [isReloadMessageShown, setIsReloadMessageShown] = useState(false);
  const location = useLocation();

  // Get sort parameters from URL query string
  const queryParams = new URLSearchParams(location.search);
  const sortKey = queryParams.get('sortKey') || '';
  const sortDir = queryParams.get('sortDir') || 'asc';

  // Helper function to determine score color based on grade (copied from StudentSidebar logic)
  const getScoreColorClass = (grade, maxScore = 100) => {
    if (grade === null || grade === undefined) {
      return 'text-gray-400';
    }

    // Calculate percentage to handle any max score value
    const percentage = (grade / maxScore) * 100;

    return percentage >= 90
      ? 'text-green-600'
      : percentage >= 80
        ? 'text-green-500'
        : percentage >= 70
          ? 'text-amber-500'
          : percentage >= 60
            ? 'text-amber-600'
            : 'text-amber-700';
  };

  return (
    <div
      className="flex flex-col fixed w-full pb-5 pt-10 bg-white px-5 mb-2 z-50"
      style={{ borderBottom: '1px solid #e5e7eb', position: 'relative' }}
    >
      {/* Simple back navigation with assignment name */}
      <div className="flex items-center justify-between mb-4">
        {/* Left side - Back button with assignment name */}
        <div className="flex items-center ml-4">
          <Link
            to={`/tclasses/${assignment?.course_id}/tassignments/${assignment_id}/tsubmissions?sortKey=${sortKey}&sortDir=${sortDir}`}
          >
            <div className="flex items-center">
              <div className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center mr-3 hover:bg-gray-100 transition-colors">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10 12L6 8L10 4"
                    stroke="black"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6 8H14"
                    stroke="black"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="flex items-center">
                <h1
                  className={`text-lg font-medium ${studentName && studentName.includes('Unmatched Submission') ? 'text-red-600 px-2 py-0.5 bg-red-50 border border-red-200 rounded-md shadow-sm' : 'text-black'}`}
                >
                  {studentName
                    ? studentName.length > 21
                      ? studentName.substring(0, 21) + '...'
                      : studentName
                    : ''}
                </h1>
              </div>
            </div>
          </Link>
        </div>

        {/* Right side */}
        <div className="flex items-center justify-between">
          {/* Total score display with color coding */}
          {submissionData && assignment && (
            <div className="flex items-center px-4 py-2 bg-gray-50 rounded-lg mr-5 border border-gray-200">
              <span className="font-medium text-gray-700 mr-2">Total:</span>
              <span
                className={`font-bold text-lg ${getScoreColorClass(submissionData.grade, assignment?.max_score || 100)}`}
              >
                {submissionData.grade !== null && submissionData.grade !== undefined
                  ? `${submissionData.grade}/${assignment?.max_score || 100}`
                  : 'Not graded'}
              </span>
            </div>
          )}

          {teachers &&
            teachers.entities?.length > 0 &&
            (teachers?.entities?.length <= 3 ? (
              (teachers?.entities ?? []).map(t => (
                <div key={t.id} className="relative flex items-center space-x-2 ml-2">
                  <span
                    id={`teacher${t.id}`}
                    className={`relative cursor-pointer text-lg w-7 h-7 rounded-full border 
                                            ${t.id === userId ? 'border-orange-200' : 'border-gray-400'} 
                                            flex items-center justify-center p-4`}
                  >
                    {t.fullName[0].toUpperCase()}
                  </span>
                  <Tooltip anchorSelect={`#teacher${t.id}`} place="bottom">
                    {t.fullName}
                  </Tooltip>
                </div>
              ))
            ) : (
              <>
                {(teachers?.entities?.slice(0, 3) ?? []).map(t => (
                  <div key={t.id} className="relative flex items-center space-x-2 ml-2">
                    <span
                      id={`teacher${t.id}`}
                      className={`relative cursor-pointer text-lg w-7 h-7 rounded-full border 
                                                ${t.id === userId ? 'border-orange-200' : 'border-gray-400'} 
                                                flex items-center justify-center p-4`}
                    >
                      {t.fullName[0].toUpperCase()}
                    </span>
                    <Tooltip anchorSelect={`#teacher${t.id}`} place="bottom">
                      {t.fullName}
                    </Tooltip>
                  </div>
                ))}
                <div className="relative flex items-center space-x-2 ml-2">
                  <span
                    id="teachers-hidden"
                    className="relative cursor-pointer text-lg w-7 h-7 rounded-full border border-gray-400 flex items-center justify-center p-4"
                  >
                    ...
                  </span>
                  <Tooltip anchorSelect="#teachers-hidden" place="bottom">
                    {(teachers?.entities?.slice(3) ?? []).map(t => (
                      <div key={t.id} className="flex gap-2 items-center justify-center">
                        <p>{t.fullName}</p>
                        {t.role === 'owner' ? (
                          <span className="px-4 inline-flex text-xs leading-4 font-semibold rounded-full bg-red-100 text-red-800">
                            I
                          </span>
                        ) : (
                          <span className="px-4 inline-flex text-xs leading-4 font-semibold rounded-full bg-green-100 text-green-800">
                            C
                          </span>
                        )}
                      </div>
                    ))}
                  </Tooltip>
                </div>
              </>
            ))}

          {/* Right side controls (Submission Navigation, Pinned Nav, etc.) */}
          <div className="flex items-center space-x-4">
            {/* Add up and down buttons for pinned question navigation */}
            {pinnedSubQuestionId && (
              <div className="flex items-center mr-2">
                <button
                  className="rounded-full w-10 h-10 flex items-center justify-center bg-amber-50 hover:bg-amber-100 text-amber-600 hover:text-amber-700 mr-2 transition-colors duration-200 border border-amber-200"
                  aria-label="Previous pinned subquestion"
                  title="Previous pinned subquestion (↑)"
                  onClick={navigateToPreviousSubQuestion}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 15l7-7 7 7"
                    ></path>
                  </svg>
                </button>

                <button
                  className="rounded-full w-10 h-10 flex items-center justify-center bg-amber-50 hover:bg-amber-100 text-amber-600 hover:text-amber-700 transition-colors duration-200 border border-amber-200"
                  aria-label="Next pinned subquestion"
                  title="Next pinned subquestion (↓)"
                  onClick={navigateToNextSubQuestion}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    ></path>
                  </svg>
                </button>
              </div>
            )}

            {/* Student navigation buttons (Back/Next Student) */}
            <div className="flex items-center space-x-2">
              <button
                className="text-gray-600 text-l font-medium flex items-center px-3 py-1.5 rounded hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={
                  !submissionIds ||
                  submissionIds.length <= 1 ||
                  !submission_id ||
                  submissionIds.indexOf(Number(submission_id)) === 0
                }
                title={
                  !submissionIds ||
                  submissionIds.length <= 1 ||
                  !submission_id ||
                  submissionIds.indexOf(Number(submission_id)) === 0
                    ? 'No previous student'
                    : 'Previous Student'
                }
                onClick={clickRight}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                <span>Back</span>
              </button>
              {/* Submission counter - Moved between Back/Next buttons */}
              <div className="px-4 py-2 bg-gray-100 rounded-lg flex items-center justify-center">
                <span className="font-medium text-gray-800">
                  {submissionIds && submissionIds.length > 0
                    ? `${submissionIds.indexOf(Number(submission_id)) + 1}/${submissionIds.length}`
                    : '1/1'}
                </span>
              </div>
              <button
                className="text-gray-600 text-l font-medium flex items-center px-3 py-1.5 rounded hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={
                  !submissionIds ||
                  submissionIds.length <= 1 ||
                  !submission_id ||
                  submissionIds.indexOf(Number(submission_id)) === submissionIds.length - 1
                }
                title={
                  !submissionIds ||
                  submissionIds.length <= 1 ||
                  !submission_id ||
                  submissionIds.indexOf(Number(submission_id)) === submissionIds.length - 1
                    ? 'No next student'
                    : 'Next Student'
                }
                onClick={clickLeft}
              >
                <span>Next</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 ml-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>

            {/* Review toggle button */}
          </div>
        </div>
      </div>

      {/* All Reviewed Popup */}
      {showAllReviewedPopup && (
        <>
          {/* Blurred background overlay */}
          <div
            className="fixed inset-0 bg-black bg-opacity-20 backdrop-blur-sm z-50"
            style={{ backdropFilter: 'blur(4px)' }}
          />

          {/* Popup */}
          <div className="fixed inset-0 flex items-center justify-center z-[99]">
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 transform transition-all animate-fade-in">
              <div className="flex items-center">
                <div className="bg-orange-100 rounded-full p-3 mr-4">
                  <img src="/assets/checkmark.png" className="size-6" alt="Success" />
                </div>
                <p className="text-lg font-medium">
                  All submissions reviewed. Taking you to the submissions list.
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NavigationHeader;
